import { isSupabaseTransportFailure } from '@/lib/utils/supabaseTransportFailure';
import type { PostgrestError } from '@supabase/supabase-js';
import { getAnonymousId, rotateAnonymousId } from './anonymousIdentity';
import { sessionRequests } from './sessionRequests';
import { isAnonymousIdentityLockConflict } from './selfIdentityWriteSerialization';

const PREPARATION_RETRY_DELAYS_MS = [100, 250] as const;
type AnonymousPreparationResponse = {
  data: boolean | null;
  error: PostgrestError | null;
  status: number;
};

export type QuizPersistenceIdentity = {
  userId: string;
  isAnonymous: boolean;
};

function waitForRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * Resolves the user identity that Postgres RPCs will see via auth.uid().
 * The shared session coordinator confirms the callback token before this chooses
 * a signed-in user or prepares the browser's anonymous identity.
 */
export async function resolveQuizPersistenceIdentity(
  fallbackUserId: string | null | undefined
): Promise<QuizPersistenceIdentity | null> {
  const assertOwner = sessionRequests.captureOwner();
  const { supabase } = await import('@/lib/supabase');
  assertOwner();
  const session = await sessionRequests.readSession(() => supabase.auth.getSession());
  assertOwner();

  if (session?.user?.id) {
    return { userId: session.user.id, isAnonymous: false };
  }

  const anonymousId = getAnonymousId() ?? fallbackUserId ?? null;
  if (!anonymousId) {
    return null;
  }

  const prepare = async (userId: string): Promise<boolean> => {
    for (let retryIndex = 0; ; retryIndex += 1) {
      assertOwner();
      let response: AnonymousPreparationResponse;
      try {
        response = await supabase.rpc('prepare_anonymous_identity', {
          p_user_id: userId,
        });
      } catch (error) {
        const retryDelay = PREPARATION_RETRY_DELAYS_MS[retryIndex];
        if (
          (!isSupabaseTransportFailure(error) && !isAnonymousIdentityLockConflict(error)) ||
          retryDelay === undefined
        ) {
          throw error;
        }
        await waitForRetry(retryDelay);
        continue;
      }

      assertOwner();
      if (response.error) {
        const retryDelay = PREPARATION_RETRY_DELAYS_MS[retryIndex];
        if (
          (!isSupabaseTransportFailure(response.error, response.status) &&
            !isAnonymousIdentityLockConflict(response.error)) ||
          retryDelay === undefined
        ) {
          throw response.error;
        }
        await waitForRetry(retryDelay);
        continue;
      }

      if (response.data === true) return true;
      if (response.data === false) return false;
      throw new Error('Unexpected response from anonymous identity preparation');
    }
  };

  const anonymousIdentityIsWritable = await prepare(anonymousId);
  if (anonymousIdentityIsWritable) {
    return { userId: anonymousId, isAnonymous: true };
  }

  assertOwner();
  const replacementId = await rotateAnonymousId(anonymousId);
  if (!(await prepare(replacementId))) {
    throw new Error('Unable to establish a writable anonymous identity');
  }
  return { userId: replacementId, isAnonymous: true };
}
